<script setup lang="ts">
import { CustomerDraft } from '@commercetools/platform-sdk';
import { FieldArray, useForm } from 'vee-validate';
import { computed, nextTick, ref } from 'vue';

import { AvsResponse, verifyAddress } from '@/api/address/verification';
import TextButton from '@/components/base/TextButton.vue';
import ThemedButton from '@/components/base/ThemedButton.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import Caption from '@/components/base/typography/Caption.vue';
import AddressForm from '@/components/checkout/AddressForm.vue';
import AvsModal from '@/components/checkout/steps/address/AvsModal.vue';
import GiftRecipientForm from '@/components/checkout/steps/address/GiftRecipientForm.vue';
import AddressBookModal from '@/components/checkout/steps/multiship/AddressBookModal.vue';
import { EditableAllocation } from '@/components/checkout/steps/multiship/MultiShip.vue';
import LineItem from '@/components/checkout/steps/multiship/MultiShipLineItem.vue';
import RemoveRemainingItemsModal from '@/components/checkout/steps/multiship/RemoveRemainingItemsModal.vue';
import { useForm as formUtils } from '@/composables/useForm';
import { NutsAddress } from '@/utils/address';
import {
  containsGiftCertificate,
  hasOnlyGiftCertificate,
  isGiftCertificateLineItem,
  needPhysicalAddress,
} from '@/utils/cart';

const props = defineProps<{
  addresses?: NutsAddress[];
  addressNumber: number;
  customer?: CustomerDraft;
  isAddAnotherPending?: boolean;
  isContinuePending?: boolean;
  isRemovalPending?: boolean;
}>();

const emit = defineEmits<{
  'remove-remaining-items': [EditableAllocation['lineItems']];
  'save-and-continue': [{ buttonName?: string }];
}>();

const formModel = useForm();
function setValidatedFieldsTouched(
  results: Awaited<ReturnType<typeof formModel.validate>>['results'],
) {
  const fields: Record<string, true> = {};
  Object.keys(results).forEach((key) => {
    fields[key] = true;
  });
  formModel.setTouched(fields);
}
const { validatorFailed } = formUtils();

const address = defineModel<NutsAddress>('address', { required: true });
const lineItems = defineModel<EditableAllocation['lineItems']>('lineItems', { required: true });

const selectedItems = computed(() => lineItems.value.filter((item) => item.quantity > 0));
const giftCertificateSelected = computed(() =>
  selectedItems.value.some((item) => isGiftCertificateLineItem(item)),
);
const productSelected = computed(() =>
  selectedItems.value.some((item) => !isGiftCertificateLineItem(item)),
);

const showSavedAddresses = computed(() => !!props.customer && !!props.addresses?.length);

const showAddressForm = computed(
  () =>
    !showSavedAddresses.value &&
    (!containsGiftCertificate(lineItems.value) || productSelected.value),
);

const showGiftCertificateForm = computed(
  () =>
    (containsGiftCertificate(lineItems.value) && giftCertificateSelected.value) ||
    hasOnlyGiftCertificate(lineItems.value),
);

const selectedCount = computed(() => selectedItems.value.length);

const remainingItems = computed(() =>
  lineItems.value.filter((i) => i.quantityAvailable - i.quantity > 0),
);

const buttonClicked = ref(false);
const showMessage = computed(
  () =>
    buttonClicked.value &&
    selectedCount.value === 0 &&
    !(props.isAddAnotherPending || props.isContinuePending || props.isRemovalPending),
);

const isButtonDisabled = computed(
  () => validatorFailed(formModel.meta.value) && selectedCount.value > 0,
);

const isAddAnotherButtonDisabled = computed(
  () => isButtonDisabled.value || remainingItems.value.length === 0,
);

const isAddressModalOpen = ref(false);
const isRemoveRemainingModalOpen = ref(false);

const avsStatus = ref<AvsResponse>();
const displayAddressVerificationModal = ref(false);
const lastButtonClicked = ref<string>();

const continueAllocatingItems = async () => {
  await nextTick();
  const { results, valid } = await formModel.validate();
  setValidatedFieldsTouched(results);
  if (!valid || selectedCount.value === 0) return;

  if (
    lastButtonClicked.value === 'continue' &&
    !address.value?.key &&
    remainingItems.value.length > 0
  ) {
    isRemoveRemainingModalOpen.value = true;
    return;
  }

  emit('save-and-continue', { buttonName: lastButtonClicked.value });
};

const addAnotherAddress = async () => {
  buttonClicked.value = true;
  const { results, valid } = await formModel.validate();
  setValidatedFieldsTouched(results);
  if (!valid || selectedCount.value === 0) return;

  lastButtonClicked.value = 'addAnother';
  avsStatus.value = needPhysicalAddress(selectedItems.value)
    ? await verifyAddress(address.value!)
    : { address: address.value!, status: 'verified' };
  if (avsStatus.value.status === 'verified') {
    continueAllocatingItems();
  } else {
    displayAddressVerificationModal.value = true;
  }
};

const handleCloseRemoveRemainingModal = (): void => {
  isRemoveRemainingModalOpen.value = false;
};

const handleRemoveRemainingCartQuantity = async () => {
  handleCloseRemoveRemainingModal();
  emit('remove-remaining-items', remainingItems.value);
};

const finishMultiShip = async () => {
  const { results, valid } = await formModel.validate();
  buttonClicked.value = true;
  if (selectedCount.value > 0) {
    setValidatedFieldsTouched(results);
    if (!valid) return;
  }

  lastButtonClicked.value = 'continue';
  avsStatus.value = needPhysicalAddress(selectedItems.value)
    ? await verifyAddress(address.value!)
    : { address: address.value!, status: 'verified' };
  if (avsStatus.value.status === 'verified') {
    if (address.value?.key || remainingItems.value.length === 0) {
      await continueAllocatingItems();
    }
    if (!address.value?.key && remainingItems.value.length > 0) {
      isRemoveRemainingModalOpen.value = true;
    }
  } else {
    displayAddressVerificationModal.value = true;
  }
};

const useSuggestedAddress = () => {
  // Should match in the sub-component that triggered this
  if (avsStatus.value && 'address' in avsStatus.value) {
    const suggestedAddress: NutsAddress = {
      ...address.value,
      ...avsStatus.value.address,
    };
    address.value = suggestedAddress;
  }
  continueAllocatingItems();
  displayAddressVerificationModal.value = false;
};

const useEnteredAddress = () => {
  displayAddressVerificationModal.value = false;
  continueAllocatingItems();
};

const confirmUnitNumber = async (unitInfo: string, updatedPostalCode?: string) => {
  const addressWithUnitNumber = {
    ...(address.value as NutsAddress),
    street2: `${address.value?.street2} ${unitInfo}`,
  };
  if (updatedPostalCode) {
    addressWithUnitNumber.postalCode = updatedPostalCode;
  }
  address.value = addressWithUnitNumber;
  continueAllocatingItems();
  displayAddressVerificationModal.value = false;
};

const confirmHouseNumber = async (houseInfo: string) => {
  const addressWithHouseNumber = {
    ...(address.value as NutsAddress),
    street1: `${houseInfo} ${address.value?.street1}`,
  };
  displayAddressVerificationModal.value = false;
  avsStatus.value = await verifyAddress(addressWithHouseNumber);
  if (avsStatus.value.status === 'verified') {
    address.value = addressWithHouseNumber;
    continueAllocatingItems();
    displayAddressVerificationModal.value = false;
  } else {
    displayAddressVerificationModal.value = true;
  }
};

const closeAvsModal = () => {
  displayAddressVerificationModal.value = false;
};
</script>

<template>
  <form @submit.prevent>
    <slot name="email" />

    <div
      data-test="active-multiship-selection"
      class="mt-5 md:mt-7"
      :class="{
        'opacity-50 pointer-events-none':
          isAddAnotherPending || isContinuePending || isRemovalPending,
      }"
    >
      <div class="flex items-center justify-between">
        <div class="flex flex-wrap items-center">
          <strong class="mr-4 text-base leading-5 md:text-lg" data-test="address-label">
            Address {{ addressNumber }}
          </strong>
          <div v-if="showSavedAddresses" class="flex items-center mr-4">
            <BaseBodyText>{{ address.street1 }}</BaseBodyText>
            <TextButton @click="isAddressModalOpen = true" class="ml-2">
              <Caption>Change</Caption>
            </TextButton>
          </div>
        </div>

        <slot name="removal" />
      </div>
      <slot name="removed" />
      <strong class="block mt-2 text-base leading-5 md:text-lg">
        Which items should we send to this address?
      </strong>
      <div data-test="unassigned-line-items">
        <div class="flex-row text-sm">
          <div class="flex-1">Product</div>
          <div class="mr-10">How Many?</div>
        </div>
        <FieldArray key-path="id" name="lineItems">
          <div v-for="(item, index) in lineItems" :key="item.id">
            <LineItem v-model="item.quantity" :index :item />
          </div>
        </FieldArray>
      </div>

      <strong
        v-if="showAddressForm && !showSavedAddresses"
        class="block mt-8 text-base leading-5 md:text-lg"
      >
        Where should we send them?
      </strong>
      <AddressForm
        v-model="address"
        class="mt-5"
        :parentForm="formModel"
        :showAddressForm
        showMakeDefault
        uniqueIdSuffix="multi-ship"
      >
        <template v-slot:giftRecipient>
          <GiftRecipientForm
            v-if="showGiftCertificateForm"
            v-model:email="address.email"
            v-model:name="address.name"
            class="mt-6 md:mt-8"
            :showName="!showAddressForm"
          />
        </template>
        <template #formControls>
          <div class="flex flex-col mt-5 md:flex-row md:mt-7">
            <ThemedButton
              type="button"
              data-test="address-multi-ship-add-address"
              theme="white"
              :disabled="isAddAnotherButtonDisabled"
              :isLoading="isAddAnotherPending"
              @click="addAnotherAddress"
            >
              Ship to Additional Address
            </ThemedButton>
            <ThemedButton
              type="button"
              class="mt-4 md:mt-0 md:ml-4"
              data-test="address-multi-ship-submit"
              theme="gray"
              :disabled="isButtonDisabled"
              :isLoading="isContinuePending"
              @click="finishMultiShip"
            >
              Continue to Delivery Options
            </ThemedButton>
          </div>
        </template>
        <template #avsModal>
          <AvsModal
            :displayAddressVerificationModal
            :avsStatus
            :addressProvided="address"
            @use-suggested-address="useSuggestedAddress"
            @use-entered-address="useEnteredAddress"
            @confirm-unit-number="confirmUnitNumber"
            @confirm-house-number="confirmHouseNumber"
            @close-avs-modal="closeAvsModal"
          />
        </template>
      </AddressForm>
      <transition name="fade">
        <div v-if="showMessage" class="mt-6 md:mt-8 text-nuts-red-800">
          <strong>You must select at least one product to continue</strong>
        </div>
      </transition>
    </div>

    <AddressBookModal
      v-if="addresses"
      v-model:address="address"
      :addresses
      :isOpen="isAddressModalOpen"
      @handle-close="isAddressModalOpen = false"
    />

    <RemoveRemainingItemsModal
      :isModalOpen="isRemoveRemainingModalOpen"
      :remainingItems
      @handle-cancel="handleCloseRemoveRemainingModal"
      @handle-close="handleCloseRemoveRemainingModal"
      @handle-confirm="handleRemoveRemainingCartQuantity"
    />
  </form>
</template>
